import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getTrainingPrograms, createTrainingProgram, deleteTrainingProgram, getTrainingEnrollments, createTrainingEnrollment, completeTrainingDay, skipTrainingDay, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaCheck, FaForward, FaGraduationCap, FaUserPlus } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Training.css';

function Training({ user }) {
  const toast = useToast();
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('programs');
  const [showModal, setShowModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [savingProgram, setSavingProgram] = useState(false);
  const [formErrorProgram, setFormErrorProgram] = useState(null);
  const [savingEnroll, setSavingEnroll] = useState(false);
  const [formErrorEnroll, setFormErrorEnroll] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    duration_days: 7,
    difficulty: 'beginner',
    tasks_json: ''
  });
  const [enrollData, setEnrollData] = useState({
    program_id: '',
    user_id: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'programs') {
        const data = await getTrainingPrograms();
        setPrograms(data.programs || data);
      } else {
        const data = await getTrainingEnrollments();
        setEnrollments(data.enrollments || data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(extractErrorMessage(error, 'Daten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    if (savingProgram) return;
    setSavingProgram(true);
    setFormErrorProgram(null);
    try {
      await createTrainingProgram({
        ...formData,
        duration_days: parseInt(formData.duration_days) || 7
      });
      setFormData({ name: '', description: '', category: '', duration_days: 7, difficulty: 'beginner', tasks_json: '' });
      setShowModal(false);
      toast.success('Trainingsprogramm erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating program:', error);
      setFormErrorProgram(extractErrorMessage(error, 'Fehler beim Erstellen des Programms'));
    } finally {
      setSavingProgram(false);
    }
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Programm wirklich löschen?')) return;
    try {
      await deleteTrainingProgram(id);
      toast.success('Programm gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Programms'));
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (savingEnroll) return;
    setSavingEnroll(true);
    setFormErrorEnroll(null);
    try {
      await createTrainingEnrollment(enrollData);
      setEnrollData({ program_id: '', user_id: '' });
      setShowEnrollModal(false);
      toast.success('Einschreibung erfolgreich');
      setActiveTab('enrollments');
      fetchData();
    } catch (error) {
      console.error('Error enrolling:', error);
      setFormErrorEnroll(extractErrorMessage(error, 'Fehler bei der Einschreibung'));
    } finally {
      setSavingEnroll(false);
    }
  };

  const handleCompleteDay = async (enrollmentId) => {
    try {
      await completeTrainingDay(enrollmentId);
      toast.success('Tag abgeschlossen');
      fetchData();
    } catch (error) {
      console.error('Error completing day:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Abschließen des Tages'));
    }
  };

  const handleSkipDay = async (enrollmentId) => {
    const reason = prompt('Grund für das Überspringen:');
    if (reason === null) return;
    try {
      await skipTrainingDay(enrollmentId, { reason });
      toast.success('Tag übersprungen');
      fetchData();
    } catch (error) {
      console.error('Error skipping day:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Überspringen des Tages'));
    }
  };

  const closeProgramModal = () => {
    if (savingProgram) return;
    setShowModal(false);
    setFormErrorProgram(null);
  };

  const closeEnrollModal = () => {
    if (savingEnroll) return;
    setShowEnrollModal(false);
    setFormErrorEnroll(null);
  };

  const openEnrollModal = (program) => {
    setSelectedProgram(program);
    setEnrollData({ program_id: program.id, user_id: '' });
    setShowEnrollModal(true);
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      beginner: { class: 'beginner', label: 'Anfänger' },
      intermediate: { class: 'intermediate', label: 'Fortgeschritten' },
      advanced: { class: 'advanced', label: 'Experte' }
    };
    return badges[difficulty] || badges.beginner;
  };

  const getProgressPercent = (enrollment) => {
    if (!enrollment.total_days || enrollment.total_days === 0) return 0;
    return Math.round((enrollment.completed_days / enrollment.total_days) * 100);
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaGraduationCap /> Training</h1>
          {activeTab === 'programs' && user.isAdmin && (
            <button className="add-btn" onClick={() => setShowModal(true)}>
              <FaPlus /> Neues Programm
            </button>
          )}
        </div>

        <div className="filter-tabs">
          <button className={activeTab === 'programs' ? 'active' : ''} onClick={() => setActiveTab('programs')}>Programme</button>
          <button className={activeTab === 'enrollments' ? 'active' : ''} onClick={() => setActiveTab('enrollments')}>Einschreibungen</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'programs' ? (
          <div className="programs-grid">
            {programs.length > 0 ? (
              programs.map(program => {
                const diffBadge = getDifficultyBadge(program.difficulty);
                return (
                  <div key={program.id} className="program-card">
                    <div className="program-header">
                      <span className={`difficulty-badge ${diffBadge.class}`}>{diffBadge.label}</span>
                      {program.category && <span className="category-tag">{program.category}</span>}
                    </div>
                    <h3 className="program-name">{program.name}</h3>
                    {program.description && <p className="program-desc">{program.description}</p>}
                    <div className="program-meta">
                      <span className="duration">{program.duration_days} Tage</span>
                    </div>
                    <div className="program-actions">
                      <button className="action-btn enroll" onClick={() => openEnrollModal(program)} title="Einschreiben">
                        <FaUserPlus /> Einschreiben
                      </button>
                      {user.isAdmin && (
                        <button className="action-btn delete" onClick={() => handleDeleteProgram(program.id)} title="Löschen">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-items">Keine Programme gefunden</div>
            )}
          </div>
        ) : (
          <div className="enrollments-list">
            {enrollments.length > 0 ? (
              enrollments.map(enrollment => {
                const progress = getProgressPercent(enrollment);
                return (
                  <div key={enrollment.id} className={`enrollment-card ${enrollment.status}`}>
                    <div className="enrollment-header">
                      <h3 className="enrollment-name">{enrollment.program_name || `Programm #${enrollment.program_id}`}</h3>
                      <span className={`status-badge ${enrollment.status}`}>{enrollment.status}</span>
                    </div>
                    <div className="progress-section">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                      </div>
                      <span className="progress-text">
                        Tag {enrollment.completed_days || 0} / {enrollment.total_days || '?'} ({progress}%)
                      </span>
                    </div>
                    {enrollment.current_task && (
                      <div className="current-task">
                        <span className="task-label">Aktuelle Aufgabe:</span>
                        <span className="task-text">{enrollment.current_task}</span>
                      </div>
                    )}
                    {enrollment.status === 'active' && (
                      <div className="enrollment-actions">
                        <button className="action-btn complete" onClick={() => handleCompleteDay(enrollment.id)} title="Tag abschließen">
                          <FaCheck /> Tag erledigt
                        </button>
                        <button className="action-btn skip" onClick={() => handleSkipDay(enrollment.id)} title="Tag überspringen">
                          <FaForward /> Überspringen
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="no-items">Keine Einschreibungen gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeProgramModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neues Trainingsprogramm</h2>
              {formErrorProgram && <div className="shared-form-error-banner">{formErrorProgram}</div>}
              <form onSubmit={handleCreateProgram}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Kategorie</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                      placeholder="z.B. Gehorsam, Service"
                    />
                  </div>
                  <div className="form-group">
                    <label>Schwierigkeit</label>
                    <select
                      value={formData.difficulty}
                      onChange={e => setFormData({...formData, difficulty: e.target.value})}
                    >
                      <option value="beginner">Anfänger</option>
                      <option value="intermediate">Fortgeschritten</option>
                      <option value="advanced">Experte</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Dauer (Tage)</label>
                  <input
                    type="number"
                    value={formData.duration_days}
                    onChange={e => setFormData({...formData, duration_days: e.target.value})}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Aufgaben (JSON, optional)</label>
                  <textarea
                    value={formData.tasks_json}
                    onChange={e => setFormData({...formData, tasks_json: e.target.value})}
                    placeholder='[{"day": 1, "task": "Beschreibung"}]'
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeProgramModal} disabled={savingProgram}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingProgram}>{savingProgram ? 'Speichert…' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEnrollModal && (
          <div className="modal-overlay" onClick={closeEnrollModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Einschreibung: {selectedProgram?.name}</h2>
              {formErrorEnroll && <div className="shared-form-error-banner">{formErrorEnroll}</div>}
              <form onSubmit={handleEnroll}>
                <div className="form-group">
                  <label>Benutzer-ID *</label>
                  <input
                    type="text"
                    value={enrollData.user_id}
                    onChange={e => setEnrollData({...enrollData, user_id: e.target.value})}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeEnrollModal} disabled={savingEnroll}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingEnroll}>{savingEnroll ? 'Speichert…' : 'Einschreiben'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Training;
